import { mergeAttributes, Node, nodePasteRule } from '@tiptap/core';
import { Node as ProseMirrorNode } from '@tiptap/pm/model';

export interface InternalLinkOptions {
  renderLabel: (props: { node: ProseMirrorNode }) => string;
  linkOnPaste: boolean;
  internalLinkRegEx: RegExp;
  plugins?: any[];
  generateResourceData: Function;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    addInternalLink: {
      /**
       * Add an image
       */
      addInternalLink: (options: { 'data-url': string; name?: string }) => ReturnType;
    };
  }
}

const getAttribute = (
  attr: string,
  el: HTMLElement,
  firstChildFallback = false
): string | null | undefined => {
  const elementsAttr = el.getAttribute(attr);
  if (firstChildFallback) return elementsAttr ?? el.firstElementChild?.getAttribute(attr);
  return elementsAttr ?? null;
};

export default Node.create<InternalLinkOptions>({
  name: 'internal-link',

  group: 'inline',
  inline: true,
  selectable: true,
  atom: true,

  addOptions() {
    return {
      ...this.parent?.(),
      renderLabel() {
        return '';
      },
      linkOnPaste: true,
    };
  },

  addAttributes() {
    return {
      'data-url': {
        default: null,
        parseHTML: (element) => getAttribute('data-url', element, true),
      },

      name: {
        default: null,
        parseHTML: (element) => getAttribute('name', element, true),
      },

      'data-icon-src': {
        default: null,
        parseHTML: (element) => getAttribute('data-icon-src', element, true),
      },

      'data-icon-type': {
        default: null,
        parseHTML: (element) => getAttribute('data-icon-type', element),
      },

      'data-icon-color': {
        default: null,
        parseHTML: (element) => getAttribute('data-icon-color', element),
      },

      'data-additional-icon-src': {
        default: null,
        parseHTML: (element) => getAttribute('data-additional-icon-src', element),
      },

      'data-additional-icon-type': {
        default: null,
        parseHTML: (element) => getAttribute('data-additional-icon-type', element),
      },

      'data-additional-icon-color': {
        default: null,
        parseHTML: (element) => getAttribute('data-additional-icon-color', element),
      },

      // this attribute is used to indicate that the link need to get its data immediately
      'data-get-resource': {
        default: null,
        parseHTML(element) {
          const getResource = element.getAttribute('data-get-resource');
          return !!getResource || null;
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'a[data-type="internal-link"]',
        priority: 60,
      },
      {
        tag: 'span.internal-link',
      },
      {
        tag: 'internal-link',
      },
    ];
  },

  addCommands() {
    return {
      addInternalLink:
        (props: any) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              ...props,
            },
          });
        },
    };
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'internal-link',
      mergeAttributes({ 'data-type': this.name }, HTMLAttributes),
      this.options.renderLabel({
        node,
      }),
    ];
  },

  addPasteRules() {
    return [
      nodePasteRule({
        find: this.options.internalLinkRegEx,
        type: this.type,
        getAttributes: (match) => {
          let [url] = match;
          const secureProtocol = 'https://';
          // check if url contains protocol
          url = !url.startsWith(secureProtocol) ? `${secureProtocol}${url}` : url;
          const { name }: any = this.options.generateResourceData(url);
          return {
            'data-url': url,
            name,
            'data-get-resource': true,
          };
        },
      }),
    ];
  },

  addProseMirrorPlugins() {
    if (!this.options.plugins) {
      return [];
    }
    return [...this.options.plugins.map((plugin: any) => plugin({ type: this.type }))];
  },
});
